<script setup lang="ts">
import { Coupon } from '@/@types/cart'
import CardChart from '@/components/shared/coupons/show/CardChart.vue'
import Badge from '@/components/ui/badge/Badge.vue'
import Button from '@/components/ui/button/Button.vue'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import Title from '@/components/ui/title/Title.vue'
import { controlStatus } from '@/helpers/coupon'
import { formatPrice } from '@/helpers/current-price'
import { formatDateFull } from '@/helpers/format-date'
import OrderLayout from '@/layouts/OrderLayout.vue'
import { Head, Link, router } from '@inertiajs/vue3'
import { ArrowLeftCircle, Calendar, Edit, Receipt, TicketPercent } from 'lucide-vue-next'
import { ref } from 'vue'

const { coupon } = defineProps<{ coupon: Coupon }>()

const copied = ref(false)

const copyCode = async () => {
    await navigator.clipboard.writeText(coupon.code ?? '')

    copied.value = true

    setTimeout(() => {
        copied.value = false
    }, 1000)
}
</script>

<template>
    <Head :title="$t('View coupon')" />

    <OrderLayout>
        <main class="flex flex-col gap-8">
            <section class="mt-6 flex items-center gap-2 text-white">
                <Link :href="`/coupons`" aria-label="Back to order">
                    <ArrowLeftCircle class="h-8 w-8" stroke-width="1.2" />
                </Link>

                <h1 class="text-2xl font-semibold">
                    {{ $t('View coupon') }}
                </h1>
            </section>
            <section class="flex flex-col gap-8 lg:flex-row">
                <Card class="flex-1 border-none">
                    <CardHeader class="mb-6 flex gap-6">
                        <div class="flex flex-col justify-between gap-y-6 md:flex-row">
                            <div class="flex items-center gap-8">
                                <Title class="cursor-pointer text-2xl" @click="copyCode">
                                    <span class="absolute rounded-lg bg-gray-100 p-3 text-base" v-if="copied">
                                        {{ $t('Copied!') }}
                                    </span>
                                    {{ coupon.code }}
                                </Title>
                                <div>
                                    <Badge :variant="controlStatus(coupon)">{{ $t(controlStatus(coupon)) }}</Badge>
                                </div>
                            </div>
                            <div class="flex items-center gap-6 md:justify-end">
                                <Switch
                                    class="cursor-pointer"
                                    :disabled="controlStatus(coupon) === 'expired' || coupon.usage_count === coupon.usage_limit"
                                    :defaultValue="coupon.status === 'active'"
                                    @update:modelValue="
                                        (value: boolean) => {
                                            router.patch(
                                                `/coupons/${coupon.id}/toggle_active`,
                                                {
                                                    status: value ? 'active' : 'disabled',
                                                },
                                                {
                                                    only: ['coupon'],
                                                    preserveScroll: true,
                                                },
                                            )
                                        }
                                    "
                                />
                                <Button
                                    :as="Link"
                                    :href="`/coupons/${coupon.id}/edit`"
                                    variant="outline"
                                    :aria-disabled="coupon.usage_count > 0"
                                    class="text-primary border-primary hover:text-primary hover:bg-gray-100/50"
                                >
                                    <Edit />
                                    {{ $t('Edit coupon') }}
                                </Button>
                            </div>
                        </div>
                        <div class="col-span-2 flex flex-wrap gap-6 text-sm text-gray-600">
                            <div class="flex items-center gap-2" v-if="coupon.start_date">
                                <Calendar class="size-5" />
                                {{ $t('Created at') }}:
                                {{ formatDateFull(coupon.start_date, $i18n.locale) }}
                            </div>
                            <div class="flex items-center gap-2" v-if="coupon.end_date">
                                <Calendar class="size-5" />
                                {{ $t('Valid until') }}:
                                {{ formatDateFull(coupon.end_date, $i18n.locale) }}
                            </div>
                            <div class="flex items-center gap-2">
                                <TicketPercent class="size-5" v-if="coupon.category == 'percentage'" />
                                <Receipt class="size-5" v-else />
                                {{ $t('Ticket type') }}:
                                {{ $t(coupon.category) }}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent class="grid gap-6 sm:grid-cols-2 2xl:grid-cols-4">
                        <Card class="h-fit max-w-lg bg-indigo-500 text-white">
                            <CardHeader>
                                <Title v-if="coupon.category == 'percentage'"> {{ coupon.percentage_value }}% </Title>
                                <Title v-else>
                                    {{ formatPrice((coupon.value_cents || 0) / 100) }}
                                </Title>
                            </CardHeader>
                            <CardContent>
                                <CardDescription class="text-base text-white">
                                    {{ $t('of Discount') }}
                                </CardDescription>
                            </CardContent>
                        </Card>
                    </CardContent>
                </Card>
                <CardChart :coupon="coupon" />
            </section>
        </main>
    </OrderLayout>
</template>
